import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import MessageInput from './MessageInput';
import ReplyOptions from './ReplyOptions';
import SignInPrompt from '../common/SignInPrompt';
import CreditPlans from '../common/CreditPlans';
import toast from 'react-hot-toast';
import { FiZap, FiChrome, FiX, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

export default function ChatArea({ chatId, onChatCreated }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [extBannerDismissed, setExtBannerDismissed] = useState(() => localStorage.getItem('rc_ext_banner_dismissed') === '1');

  const dismissExtBanner = () => {
    localStorage.setItem('rc_ext_banner_dismissed', '1');
    setExtBannerDismissed(true);
  };
  const messagesEndRef = useRef(null);
  const { user, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Verify payment after Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const purchase = searchParams.get('purchase');
    if (purchase === 'success' && sessionId && user) {
      verifyPayment(sessionId);
      setSearchParams({});
    }
  }, [user, searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      const { data } = await api.post('/credits/verify-payment', { sessionId });
      if (data.added) {
        updateUser({ credits: data.credits });
        toast.success(`${data.added.toLocaleString()} credits added! You now have ${data.credits.toLocaleString()} credits.`);
      } else {
        updateUser({ credits: data.credits });
      }
    } catch (err) {
      console.error('Payment verification error:', err);
    }
  };

  useEffect(() => {
    if (chatId && user) loadChat(chatId);
    else setMessages([]);
  }, [chatId, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChat = async (id) => {
    try {
      const { data } = await api.get(`/chat/${id}`);
      setMessages(data.messages || []);
    } catch {
      // silent
    }
  };

  const handleSubmit = async (message, tones, replyInstructions, replyLength, options = {}) => {
    // If not logged in, show sign-in prompt
    if (!user) {
      setShowSignIn(true);
      return;
    }

    // If no credits, show plans
    if ((user.credits || 0) <= 0) {
      setShowPlans(true);
      toast.error('No credits remaining. Please purchase more.');
      return;
    }

    const displayContent = options.mode === 'fix'
      ? `✏️ Fix My Draft:\n${options.draft}${message ? `\n\n📩 Original email:\n${message}` : ''}`
      : message;
    setMessages(prev => [...prev, { role: 'user', content: displayContent, _id: Date.now() }]);
    setLoading(true);

    try {
      const { data } = await api.post('/chat/generate', {
        message,
        tones,
        chatId,
        replyInstructions,
        replyLength,
        mode: options.mode || 'reply',
        draft: options.draft || '',
        language: options.language || '',
        category: options.category || ''
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.summary || 'Here are your replies:',
        replies: data.replies,
        _id: Date.now() + 1
      }]);

      if (data.chatId && !chatId) {
        onChatCreated(data.chatId);
      }

      // Update credit balance
      if (typeof data.credits === 'number') {
        updateUser({ credits: data.credits });
        if (data.creditsUsed) {
          toast.success(`Used ${data.creditsUsed} credits. ${data.credits.toLocaleString()} remaining.`);
        }
      }
    } catch (err) {
      const errData = err.response?.data;
      if (err.response?.status === 402 || errData?.needsCredits) {
        setShowPlans(true);
        toast.error('No credits remaining. Please purchase more.');
      } else {
        toast.error(errData?.error || 'Failed to generate replies.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = () => {
    if (!user) {
      setShowSignIn(true);
    }
  };

  const isEmpty = messages.length === 0 && !loading;

  return (
    <div style={styles.container}>
      {!extBannerDismissed && (
        <div style={styles.extBanner}>
          <FiChrome size={16} style={{ flexShrink: 0 }} />
          <span style={styles.extBannerText}>
            <strong>New!</strong> Reply directly inside Gmail with our Chrome Extension
          </span>
          <Link to="/extension" style={styles.extBannerCta}>
            Get it free <FiArrowRight size={14} />
          </Link>
          <button onClick={dismissExtBanner} style={styles.extBannerClose} aria-label="Dismiss">
            <FiX size={14} />
          </button>
        </div>
      )}
      {isEmpty ? (
        <div style={styles.centeredLayout}>
          <div style={styles.centeredContent}>
            <div style={styles.emptyIcon}>
              <img src="/logo.png" alt="ReplyCraft" style={{ width: 52, height: 52, borderRadius: 12 }} />
            </div>
            <h2 style={styles.emptyTitle}>ReplyCraft</h2>
            <p style={styles.emptySubtitle}>Paste any email or message and get instant reply options in multiple tones</p>

            <div style={styles.centeredInput}>
              <MessageInput onSubmit={handleSubmit} loading={loading} disabled={false} onFocus={handleInputFocus} />
            </div>

            <div style={styles.features}>
              {[
                'Professional, warm & assertive tones',
                'Copy any reply with one click',
                'Follow-up? Paste in the same chat for smarter, tailored replies'
              ].map(f => (
                <div key={f} style={styles.featureItem}>
                  <span style={styles.featureDot} />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={styles.messagesArea}>
            {messages.map((msg) => (
              <div key={msg._id} style={styles.messageWrapper}>
                <div style={{
                  ...styles.message,
                  ...(msg.role === 'user' ? styles.userMessage : styles.assistantMessage)
                }}>
                  {msg.role === 'user' ? (
                    <div style={styles.userBubble}>
                      <p style={styles.messageText}>{msg.content}</p>
                    </div>
                  ) : (
                    <div style={styles.assistantContent}>
                      {msg.replies && msg.replies.length > 0 ? (
                        <ReplyOptions replies={msg.replies} />
                      ) : (
                        <p style={styles.messageText}>{msg.content}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div style={styles.messageWrapper}>
                <div style={{ ...styles.message, ...styles.assistantMessage }}>
                  <div style={styles.typingIndicator}>
                    <div style={styles.dot} />
                    <div style={{ ...styles.dot, animationDelay: '0.2s' }} />
                    <div style={{ ...styles.dot, animationDelay: '0.4s' }} />
                    <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 14 }}>Generating replies...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up tip after first reply */}
            {messages.length === 2 && !loading && (
              <div style={styles.followUpTip}>
                <span style={styles.tipIcon}>&#128161;</span>
                <span>Got a follow-up? Paste it right here in the same chat — ReplyCraft will remember the conversation and give you a smarter, tailored reply.</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <MessageInput onSubmit={handleSubmit} loading={loading} disabled={false} />
        </>
      )}

      <SignInPrompt isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
      <CreditPlans isOpen={showPlans} onClose={() => setShowPlans(false)} currentCredits={user?.credits || 0} />

      <style>{`
        @keyframes bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }
      `}</style>
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%' },
  extBanner: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
    background: 'linear-gradient(90deg, var(--accent), #4f46e5)', color: '#fff',
    fontSize: 13, flexWrap: 'wrap', borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  extBannerText: { flex: 1, minWidth: 200 },
  extBannerCta: {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px',
    background: 'rgba(255,255,255,0.2)', color: '#fff', textDecoration: 'none',
    borderRadius: 14, fontSize: 12, fontWeight: 600, transition: 'background 0.2s'
  },
  extBannerClose: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
    cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 4
  },
  centeredLayout: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px 16px' },
  centeredContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: 700 },
  centeredInput: { width: '100%', marginTop: 32, marginBottom: 32 },
  messagesArea: { flex: 1, overflowY: 'auto', padding: '20px 16px' },
  emptyIcon: { width: 80, height: 80, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 },
  emptyTitle: { fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 },
  emptySubtitle: { fontSize: 'clamp(14px, 3vw, 16px)', color: 'var(--text-secondary)', maxWidth: 460, lineHeight: 1.6 },
  features: { display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' },
  featureItem: { display: 'flex', alignItems: 'flex-start', gap: 10, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5 },
  featureDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 6 },
  messageWrapper: { maxWidth: 800, margin: '0 auto 20px', width: '100%' },
  message: { display: 'flex' },
  userMessage: { justifyContent: 'flex-end' },
  assistantMessage: { justifyContent: 'flex-start' },
  userBubble: { background: '#c7d2fe', borderRadius: '16px 16px 4px 16px', padding: '12px 18px', maxWidth: '80%' },
  messageText: { fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' },
  assistantContent: { width: '100%' },
  typingIndicator: { display: 'flex', alignItems: 'center', padding: '16px' },
  dot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--text-muted)', marginRight: 4, animation: 'bounce 1.4s infinite' },
  followUpTip: {
    maxWidth: 800, margin: '0 auto 16px', padding: '12px 16px', background: 'var(--accent-dim)',
    border: '1px solid var(--accent)', borderRadius: 10, display: 'flex', alignItems: 'flex-start',
    gap: 10, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5
  },
  tipIcon: { fontSize: 18, flexShrink: 0, marginTop: 1 }
};
